import { useState, useMemo, ReactElement, useEffect } from 'react';
import { getIconNameFromFileName } from 'BambooHR.util';
import moment from 'moment.lib';
import classNames from 'classnames';
import { FileDetails, SelectedFiles } from '../../types';
import useStyles from './file-list-item.styles';
import { Checkbox, FileIcon, Flex, RequiredToggle, Tooltip } from '@bamboohr/fabric';
import FileCard from 'file-card.mod';

interface Props {
	file: FileDetails;
	handleFileSelection: (file: SelectedFiles, checked: boolean) => void
	handleToggleRequired: (fileId: string, checked: boolean) => void
	editFileProperty: (fileId: string, property: string, value: string) => void
	tooltipScrollOffset: number
}

export function FileListItem(props: Props): ReactElement {
	const { file, handleFileSelection, handleToggleRequired, tooltipScrollOffset, editFileProperty } = props;

	const styles = useStyles({ offset: tooltipScrollOffset });
	const [isSelected, setIsSelected] = useState(file.meta.checked || false);
	const [isRequired, setIsRequired] = useState(file.meta.required === 'yes');
	
	const hasEsignatures = useMemo(() => {
		return file.esignature_template_id !== null &&
		file.has_esignatures &&
		file.esignatures.has_fields;
	}, [file.esignature_template_id, file.has_esignatures, file.esignatures.has_fields]);

	// The fileIdString is split up as `{fileId}-{isEsigTemplate}-{isRequired}`
	// This only applies to selected files, we should not change the id of the original files
	const fileIdString = useMemo(() => {
		const isEsig = hasEsignatures ? 'yes' : 'no'
		const isEsigRequired = isRequired ? 'yes' : 'no'

		return `${file.id}-${isEsig}-${isEsigRequired}`
	}, [hasEsignatures, isRequired]);

	const handleSelectionChange = () => {
		if (!file?.meta?.disabled) {
			handleFileSelection(
				{
					...FileCard.fileCardFromFile(file),
					required: isRequired ? 'yes' : 'no',
				},
				!isSelected,
			);
			setIsSelected(!isSelected);
		}
	};

	const handleRequiredChange = () => {
		handleToggleRequired(file.id, !isRequired);
		setIsRequired(!isRequired);
	};

	useEffect(() => {
		if(isSelected){
			editFileProperty(file.id, 'id', fileIdString);
		}
	}, [fileIdString, isSelected])

	const rowClasses = classNames(styles.fileListItem, {
		[styles.active]: isSelected,
		[styles.disabled]: file.meta.disabled,
		'show-esignature-template': file.esignature_template_id && file.esignatures && file.esignatures.has_fields,
	});

	const fileCreated = moment(file.created).format(moment.convert.jQuery(window.GLOBAL_DATEPICKER_MASK));
	let createdStr;

	if (file.created_str) {
		createdStr = file.created_str;
	} else if (file.created_user_id > 0) {
		createdStr = $.__('Created %1$s by %2$s', fileCreated, file.created_user);
	} else {
		createdStr = $.__('Created %1$s', fileCreated);
	}

	const selectionCheckboxId = `check${ file.id }-${ hasEsignatures ? 'yes' : 'no' }-${ isRequired }`;
	const requiredCheckboxId = `${ file.id }-toggle-${ isRequired }`;
	return (
		<>
			<li>
				<div
					className={ rowClasses }
					onKeyDown={ () => null }
					role="button"
					tabIndex={ 0 }
				>
					<Flex flex={1} maxWidth='calc(100% - 54px)' onClick={handleSelectionChange}>
						<div className={ classNames(styles.check) }>
							<Checkbox
								checked={ isSelected }
								disabled={ file.meta.disabled }
								id={ selectionCheckboxId } 
								onChange={(p, e) => {
										e.preventDefault();
										e.stopPropagation();
									}}
								required={ isRequired }
								value=''
							/>								
						</div>
						<div className={ classNames(styles.icon) }>
							<FileIcon
								filename={file.original_file_name}
								size="small"
								type={hasEsignatures ? 'esig' : undefined}
							/>
						</div>
						<div className={ classNames(styles.info) }>
							<span className={ classNames(styles.infoLine, styles.infoTitle, 'truncate') }>{ file.name }</span>
							<span className="hidden">{ file.can_see_file }</span>
							<span className={ classNames(styles.infoLine, styles.infoInfo) }>
								{ `${ createdStr } (${ window.BambooHR.Utils.humanFileSize(file.size) })` }
							</span>
						</div>
					</Flex>
					{hasEsignatures && (
						<div className={ classNames(styles.actions) }>
							<Tooltip
								content={$.__('Click to make signature %1', isRequired ? 'optional' : 'required')}
								title={$.__('Signature %1', isRequired ? 'required' : 'optional')}
							>
								<RequiredToggle
									id={requiredCheckboxId}
									isChecked={isRequired}
									onChange={handleRequiredChange}
								/>	
							</Tooltip>
						</div>
					)}
				</div>
			</li>
		</>
	);

}
